import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import QuickActions from '../components/QuickActions';
import { useTeam } from '../contexts/TeamContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Heart, Gift, Plus, Check, User, Calendar, MessageCircle } from 'lucide-react';

const API_URL = window.location.origin || '';

const Gratitude = () => {
  const { currentTeam } = useTeam();
  const [loading, setLoading] = useState(true);
  const [gratitudeEntries, setGratitudeEntries] = useState([]);
  const [generalGratitudes, setGeneralGratitudes] = useState([]);
  const [filter, setFilter] = useState('all'); // all, pending, thanked

  useEffect(() => {
    if (currentTeam) {
      fetchGratitudeEntries();
    }
  }, [currentTeam]);

  const fetchGratitudeEntries = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/gratitude`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setGratitudeEntries(data);
      }
    } catch (error) {
      console.error('Error fetching gratitude entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEntries = gratitudeEntries.filter(entry => {
    if (filter === 'all') return true;
    if (filter === 'pending') return entry.acknowledgment_status === 'pending';
    if (filter === 'thanked') return entry.acknowledgment_status !== 'pending';
    return true;
  });

  const pendingCount = gratitudeEntries.filter(e => e.acknowledgment_status === 'pending').length;

  if (!currentTeam) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto fade-in">
          <h1 className="text-4xl font-bold mb-6">Gratitude</h1>
          <Card className="p-8">
            <p className="text-gray-600">Please select a team to track gratitude and kindnesses.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-4xl font-bold">Gratitude</h1>
            <p className="text-gray-600 mt-2">Track kindnesses and express gratitude</p>
            {pendingCount > 0 && (
              <p className="text-sm text-orange-600 mt-1 font-medium">
                {pendingCount} kindness{pendingCount !== 1 ? 'es' : ''} awaiting thanks
              </p>
            )}
          </div>
          <div className="flex gap-3">
            <AddGratitudeDialog onSuccess={fetchGratitudeEntries} />
            <QuickActions position="top" />
          </div>
        </div>

        <Tabs defaultValue="kindnesses" className="space-y-6">
          <TabsList>
            <TabsTrigger value="kindnesses">
              <Gift className="w-4 h-4 mr-2" />
              Acts of Kindness
            </TabsTrigger>
            <TabsTrigger value="general">
              <Heart className="w-4 h-4 mr-2" />
              General Gratitude
            </TabsTrigger>
          </TabsList>

          {/* Acts of Kindness Tab */}
          <TabsContent value="kindnesses">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Acts of Kindness Received</h2>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={filter === 'all' ? 'default' : 'outline'}
                    onClick={() => setFilter('all')}
                    className={filter === 'all' ? 'bg-blue-600' : ''}
                  >
                    All ({gratitudeEntries.length})
                  </Button>
                  <Button
                    size="sm"
                    variant={filter === 'pending' ? 'default' : 'outline'}
                    onClick={() => setFilter('pending')}
                    className={filter === 'pending' ? 'bg-orange-600' : ''}
                  >
                    Pending ({pendingCount})
                  </Button>
                  <Button
                    size="sm"
                    variant={filter === 'thanked' ? 'default' : 'outline'}
                    onClick={() => setFilter('thanked')}
                    className={filter === 'thanked' ? 'bg-green-600' : ''}
                  >
                    Thanked ({gratitudeEntries.length - pendingCount})
                  </Button>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : filteredEntries.length === 0 ? (
                <div className="text-center py-12">
                  <Gift className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 font-medium">
                    {filter === 'pending' ? 'No pending thank-yous' : 
                     filter === 'thanked' ? 'No completed thank-yous yet' :
                     'No acts of kindness recorded yet'}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">Track the kind things people do to help your family</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredEntries.map(entry => (
                    <KindnessCard key={entry.id} entry={entry} onUpdate={fetchGratitudeEntries} />
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* General Gratitude Tab */}
          <TabsContent value="general">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">General Gratitude</h2>
                <AddGeneralGratitudeDialog generalGratitudes={generalGratitudes} setGeneralGratitudes={setGeneralGratitudes} />
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Reflect on things you're grateful for during challenging times - moments, progress, small joys, or silver linings.
              </p>
              {generalGratitudes.length === 0 ? (
                <div className="text-center py-12">
                  <Heart className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 font-medium">No gratitude entries yet</p>
                  <p className="text-sm text-gray-500 mt-2">Start recording what you're grateful for</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {generalGratitudes.map(entry => (
                    <Card key={entry.id} className="p-4 border-2">
                      <div className="flex items-start gap-3">
                        <Heart className="w-5 h-5 text-red-500 mt-1 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-gray-900">{entry.text}</p>
                          <p className="text-xs text-gray-500 mt-1">{entry.date}</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>

        <div className="mt-8">
          <QuickActions position="bottom" />
        </div>
      </div>
    </Layout>
  );
};

// Kindness Card Component
const KindnessCard = ({ entry, onUpdate }) => {
  const [showThankDialog, setShowThankDialog] = useState(false);
  const [thankMethod, setThankMethod] = useState('');
  const [thankNotes, setThankNotes] = useState('');
  const [thanking, setThanking] = useState(false);

  const handleMarkThanked = async () => {
    if (!thankMethod) {
      toast.error('Please select how you thanked them');
      return;
    }

    setThanking(true);
    try {
      const response = await fetch(`${API_URL}/api/teams/${entry.team_id}/gratitude/${entry.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          acknowledgment_status: thankMethod,
          acknowledgment_details: thankNotes,
          acknowledged_date: new Date().toISOString()
        })
      });

      if (response.ok) {
        toast.success('Marked as thanked!');
        setShowThankDialog(false);
        if (onUpdate) onUpdate();
      } else {
        toast.error('Failed to update');
      }
    } catch (error) {
      console.error('Error updating:', error);
      toast.error('Error updating');
    } finally {
      setThanking(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'pending': { color: 'bg-orange-100 text-orange-800 border-orange-300', text: 'Pending' },
      'thanked_verbally': { color: 'bg-green-100 text-green-800 border-green-300', text: 'Thanked Verbally' },
      'sent_card': { color: 'bg-blue-100 text-blue-800 border-blue-300', text: 'Sent Card' },
      'sent_donation': { color: 'bg-purple-100 text-purple-800 border-purple-300', text: 'Made Donation' },
      'social_media': { color: 'bg-pink-100 text-pink-800 border-pink-300', text: 'Social Media' },
      'other': { color: 'bg-gray-100 text-gray-800 border-gray-300', text: 'Other' }
    };
    const badge = badges[status] || badges['other'];
    return <Badge className={`${badge.color} border`}>{badge.text}</Badge>;
  };

  const getKindnessIcon = (type) => {
    switch(type) {
      case 'meal': return '🍽️';
      case 'donation': return '💵';
      case 'childcare': return '👶';
      case 'transportation': return '🚗';
      case 'emotional_support': return '❤️';
      case 'yard_work': return '🌱';
      case 'housework': return '🏠';
      case 'flowers': return '💐';
      default: return '🎁';
    }
  };

  return (
    <Card className="p-4 border-2 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl">{getKindnessIcon(entry.kindness_type)}</span>
            <div>
              <h3 className="font-semibold text-lg text-gray-900">{entry.title}</h3>
              <p className="text-sm text-gray-600">{entry.description}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 text-sm">
            <div>
              <span className="text-gray-600">From:</span>
              <span className="font-medium text-gray-900 ml-2">{entry.giver_name}</span>
            </div>
            <div>
              <span className="text-gray-600">Date:</span>
              <span className="text-gray-900 ml-2">{new Date(entry.date_of_kindness).toLocaleDateString()}</span>
            </div>
            {entry.giver_contact && (
              <div className="col-span-2">
                <span className="text-gray-600">Contact:</span>
                <span className="text-gray-900 ml-2">{entry.giver_contact}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 mt-3">
            {getStatusBadge(entry.acknowledgment_status)}
            {entry.acknowledged_date && (
              <span className="text-xs text-gray-500">
                on {new Date(entry.acknowledged_date).toLocaleDateString()}
              </span>
            )}
          </div>

          {entry.acknowledgment_details && (
            <p className="text-sm text-gray-600 mt-2 italic">"{entry.acknowledgment_details}"</p>
          )}
        </div>

        <div>
          {entry.acknowledgment_status === 'pending' ? (
            <Dialog open={showThankDialog} onOpenChange={setShowThankDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-green-600 hover:bg-green-700">
                  <Check className="w-4 h-4 mr-1" />
                  Mark Thanked
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Mark as Thanked</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>How did you thank them? *</Label>
                    <select
                      value={thankMethod}
                      onChange={(e) => setThankMethod(e.target.value)}
                      className="w-full border-2 rounded-md px-3 py-2 mt-1"
                    >
                      <option value="">Select method...</option>
                      <option value="thanked_verbally">Verbally (in person or phone)</option>
                      <option value="sent_card">Sent thank you card</option>
                      <option value="sent_donation">Made donation to their preferred charity</option>
                      <option value="social_media">Social media post/message</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label>Notes (optional)</Label>
                    <Textarea
                      value={thankNotes}
                      onChange={(e) => setThankNotes(e.target.value)}
                      placeholder="Any notes about thanking them"
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => setShowThankDialog(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleMarkThanked}
                      disabled={thanking || !thankMethod}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      {thanking ? 'Saving...' : 'Mark as Thanked'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          ) : (
            <Badge className="bg-green-100 text-green-800 border border-green-300">
              <Check className="w-3 h-3 mr-1" />
              Thanked
            </Badge>
          )}
        </div>
      </div>
    </Card>
  );
};

// Add Gratitude Dialog
const AddGratitudeDialog = ({ onSuccess }) => {
  const { currentTeam } = useTeam();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    kindness_type: 'other',
    giver_name: '',
    giver_contact: '',
    date_of_kindness: new Date().toISOString().split('T')[0]
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        date_of_kindness: new Date(formData.date_of_kindness).toISOString()
      };

      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/gratitude`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Kindness recorded successfully');
        setOpen(false);
        setFormData({
          title: '',
          description: '',
          kindness_type: 'other',
          giver_name: '',
          giver_contact: '',
          date_of_kindness: new Date().toISOString().split('T')[0]
        });
        if (onSuccess) onSuccess();
      } else {
        toast.error('Failed to record kindness');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error recording kindness');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Record Kindness
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Record an Act of Kindness</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>What kind thing did they do? *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Brought dinner, Mowed the lawn, Sent flowers"
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label>Type of kindness</Label>
            <select
              value={formData.kindness_type}
              onChange={(e) => setFormData({ ...formData, kindness_type: e.target.value })}
              className="w-full border-2 rounded-md px-3 py-2 mt-1"
            >
              <option value="meal">Meal / Food</option>
              <option value="flowers">Flowers / Gift</option>
              <option value="yard_work">Yard Work / Lawn Care</option>
              <option value="housework">Housework / Cleaning</option>
              <option value="childcare">Childcare</option>
              <option value="transportation">Transportation / Errands</option>
              <option value="donation">Financial / Donation</option>
              <option value="emotional_support">Emotional Support / Visit</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <Label>Additional details</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Any additional details about what they did"
              rows={2}
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Who did this? *</Label>
              <Input
                value={formData.giver_name}
                onChange={(e) => setFormData({ ...formData, giver_name: e.target.value })}
                placeholder="Person or family name"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>Their contact info (optional)</Label>
              <Input
                value={formData.giver_contact}
                onChange={(e) => setFormData({ ...formData, giver_contact: e.target.value })}
                placeholder="Phone or email"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label>When did this happen? *</Label>
            <Input
              type="date"
              value={formData.date_of_kindness}
              onChange={(e) => setFormData({ ...formData, date_of_kindness: e.target.value })}
              required
              className="mt-1"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-blue-600 hover:bg-blue-700">
              {loading ? 'Recording...' : 'Record Kindness'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Add General Gratitude Dialog
const AddGeneralGratitudeDialog = ({ generalGratitudes, setGeneralGratitudes }) => {
  const [open, setOpen] = useState(false);
  const [gratitudeText, setGratitudeText] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!gratitudeText.trim()) return;

    const newEntry = {
      id: Date.now(),
      text: gratitudeText,
      date: new Date().toLocaleDateString()
    };

    setGeneralGratitudes([newEntry, ...generalGratitudes]);
    toast.success('Gratitude recorded');
    setGratitudeText('');
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Gratitude
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>What Are You Grateful For?</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>I'm grateful for...</Label>
            <Textarea
              value={gratitudeText}
              onChange={(e) => setGratitudeText(e.target.value)}
              placeholder="A moment of progress, a small joy, someone's patience, a silver lining..."
              rows={4}
              className="mt-1"
              required
            />
          </div>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
              Add Gratitude
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Gratitude;
